import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { DeckSpec } from "../lib/claude";
import type { DeckImages, DeckAspect } from "../lib/pptxBuilder";

export interface LibraryEntry {
  id: string;
  createdAt: number;
  clientId?: string;
  taskId?: string;
  taskTitle: string;
  presenterId: string;
  aspect: DeckAspect;
  deck: DeckSpec;
  images: DeckImages;
  /** flags de uso */
  publishedToIG?: boolean;
  publishedAt?: number;
  igPostId?: string;
}

interface State {
  entries: LibraryEntry[];
  add: (e: Omit<LibraryEntry, "id" | "createdAt">) => string;
  remove: (id: string) => void;
  update: (id: string, patch: Partial<LibraryEntry>) => void;
  /** apaga images base64 grandes pra economizar localStorage */
  compactImages: (id: string) => void;
}

const uid = () => "lib" + Math.random().toString(36).slice(2, 10);

/** Storage seguro que descarta dataURLs se cota estourar. */
const safeStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    try { localStorage.setItem(name, value); }
    catch (e: any) {
      if (e?.name !== "QuotaExceededError" && !/quota/i.test(String(e?.message))) throw e;
      try {
        const parsed = JSON.parse(value);
        const entries = (parsed?.state?.entries ?? []).slice(0, 20).map((en: any) => ({
          ...en,
          images: {}  // limpa todas as imagens nos entries antigos
        }));
        const compact = { ...parsed, state: { ...parsed.state, entries } };
        localStorage.setItem(name, JSON.stringify(compact));
        setTimeout(() => alert("⚠ Biblioteca grande demais. Auto-compactei (imagens removidas das entradas antigas)."), 100);
      } catch { localStorage.removeItem(name); }
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name)
}));

export const useLibrary = create<State>()(
  persist(
    (set) => ({
      entries: [],
      add: (e) => {
        const id = uid();
        set(s => ({
          entries: [{ ...e, id, createdAt: Date.now() }, ...s.entries].slice(0, 50)
        }));
        return id;
      },
      remove: (id) => set(s => ({ entries: s.entries.filter(x => x.id !== id) })),
      update: (id, patch) => set(s => ({
        entries: s.entries.map(x => x.id === id ? { ...x, ...patch } : x)
      })),
      compactImages: (id) => set(s => ({
        entries: s.entries.map(x => x.id === id ? { ...x, images: {} } : x)
      }))
    }),
    { name: "agencia-vpc-library-v1", storage: safeStorage }
  )
);
